/**
 * Página de Inicio Inmersiva
 * Diseño dinámico tipo Heinz/Awwwards con scrollytelling
 * Contenido según guía oficial de estructura
 * 
 * Secciones:
 * 1. Hero principal a pantalla completa
 * 2. Bloque "Compromiso con lo que te gusta"
 * 3. Categorías de productos (Retail, Horeca)
 * 4. Recetas destacadas
 * 5. Cierre emocional "Somos Intercasa. Somos Canarias."
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import {
  AnimatedSection,
  AnimatedText,
  ParallaxImage,
  MagneticButton,
  HeroSection,
} from '../components/animations';
import { getRecipeById } from '../data/recipesData';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de inicio con diseño inmersivo
 * Estructura según guía oficial de Intercasa
 * @returns {JSX.Element} Página de inicio
 */
const HomePage = () => {
  const compromiseRef = useRef(null);
  const ctaRef = useRef(null);

  // Categorías de productos según guía
  const productCategories = [
    {
      id: 'retail',
      name: 'Productos Retail',
      description: 'Para el día a día de los hogares canarios',
      link: '/productos/retail',
      image: '/images/inicio/productos-retail.webp',
    },
    {
      id: 'horeca',
      name: 'Productos Horeca',
      description: 'Para hostelería y restauración',
      link: '/productos/horeca',
      image: '/images/inicio/productos-horeca.webp',
    },
  ];

  // Recetas destacadas (3 según guía) - IDs: 1 (Pollo glaseado), 4 (Costillas BBQ), 10 (Papas bravas)
  const featuredRecipes = [
    getRecipeById(1),  // Pollo glaseado con ketchup y miel
    getRecipeById(4),  // Costillas BBQ al horno
    getRecipeById(10), // Papas bravas canarias con alioli
  ].filter(Boolean);

  // Animaciones de scroll personalizadas
  useEffect(() => {
    const compromiseSection = compromiseRef.current;
    if (compromiseSection) {
      const textElements = compromiseSection.querySelectorAll('.story-text');
      
      textElements.forEach((text, index) => {
        gsap.fromTo(text,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            delay: index * 0.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: text,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO PRINCIPAL - Pantalla completa
        Título: "Sabor de tradición canaria."
        Subtítulo: "Salsas y productos elaborados en Canarias..."
        CTA: Ver productos
        =============================================
      */}
      <HeroSection
        title="Sabor de tradición canaria"
        subtitle="Salsas y productos elaborados en Canarias para disfrutar cada día"
        backgroundVideo="/videos/tomates-optimized.mp4"
        height={100}
        textPosition="center"
        overlayGradient="linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)"
      >
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
          <Link to="/productos">
            <MagneticButton variant="primary" size="lg">
              Ver productos
            </MagneticButton>
          </Link>
        </div>
      </HeroSection>

      {/* 
        =============================================
        BLOQUE "NUESTROS IMPRESCINDIBLES"
        3 productos destacados con imagen y descripción
        Paleta: Fondo Rojo Intercasa (#C1052F), Cards blancas
        =============================================
      */}
      <section className="relative bg-intercasa-red py-24 md:py-32 overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-intercasa-yellow/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          {/* Título de sección */}
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl mb-4">
              Clásicos Intercasa
            </h2>
            <p className="text-center font-lato text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              Los sabores que nunca faltan en tu mesa
            </p>
          </AnimatedSection>

          {/* Grid de productos imprescindibles - Fila superior: Ketchup y Barbacoa */}
          <div className="grid md:grid-cols-6 gap-8 max-w-6xl mx-auto mb-8">
            {/* Ketchup - El de toda la vida */}
            <AnimatedSection animation="fadeUp" delay={0.1} className="group md:col-span-2 md:col-start-2">
              <Link to="/productos/retail/ketchup-cristal" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/ketchup.webp" 
                      alt="Ketchup Intercasa - El de toda la vida"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      El de toda la vida
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      El de siempre, con el sabor que nos acompaña desde generaciones. Nuestro ketchup clásico, elaborado en Canarias con tomates maduros y todo el cariño de casa.
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>Ver producto</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </Link>
            </AnimatedSection>

            {/* Barbacoa Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.2} className="group md:col-span-2">
              <Link to="/productos/retail/salsa-barbacoa-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/barbacoa.webp" 
                      alt="Barbacoa Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Barbacoa Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      La de los momentos especiales, con ese sabor intenso que invita a compartir. Nuestra salsa barbacoa, elaborada en Canarias, combina notas dulces y ahumadas que realzan carnes, hamburguesas y platos a la parrilla con un toque inconfundible.
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>Ver producto</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </Link>
            </AnimatedSection>
          </div>

          {/* Grid de productos imprescindibles - Fila inferior: Mayonesa, Mostaza y Alioli */}
          <div className="grid md:grid-cols-6 gap-8 max-w-6xl mx-auto">
            {/* Mayonesa Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.3} className="group md:col-span-2">
              <Link to="/productos/retail/mayonesa-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/mayonesa.webp" 
                      alt="Mayonesa Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Mayonesa Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      La de cada día, con la cremosidad que siempre apetece. Nuestra mayonesa, hecha en Canarias y pensada para todos los hogares, aporta un sabor equilibrado y natural que convierte cualquier comida en algo especial.
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>Ver producto</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </Link>
            </AnimatedSection>

            {/* Mostaza Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.4} className="group md:col-span-2">
              <Link to="/productos/retail/mostaza-dulce-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/mostaza.webp" 
                      alt="Mostaza Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Mostaza Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      La suave y equilibrada, perfecta para acompañar sin enmascarar. Nuestra mostaza, elaborada en Canarias, aporta un punto delicado y ligeramente dulce que encaja a la perfección en bocadillos, carnes y recetas del día a día.
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>Ver producto</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </Link>
            </AnimatedSection>

            {/* Alioli Intercasa */}
            <AnimatedSection animation="fadeUp" delay={0.5} className="group md:col-span-2">
              <Link to="/productos/retail/alioli-receta-canaria-pe" className="block h-full">
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img 
                      src="/images/inicio/alioli.webp" 
                      alt="Alioli Intercasa"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex-grow flex flex-col bg-white">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl mb-3">
                      Alioli Intercasa
                    </h3>
                    <p className="font-lato text-intercasa-dark/70 text-base leading-relaxed mb-4">
                      El de casa, con ese toque canario que nunca falla. Nuestro alioli, elaborado en Canarias con una receta tradicional, combina la intensidad del ajo con una textura suave que acompaña cualquier plato como si estuvieras en familia.
                    </p>
                    {/* CTA */}
                    <div className="flex items-center text-intercasa-red font-lato font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform duration-300">
                      <span>Ver producto</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Línea decorativa */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. BLOQUE "COMPROMISO CON LO QUE TE GUSTA"
        Diseño: 2 columnas (Imagen + Texto)
        =============================================
      */}
      <section ref={compromiseRef} className="relative bg-white overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Columna de imagen - Fábrica, ingredientes o proceso */}
          <div className="relative h-[50vh] lg:h-auto overflow-hidden order-2 lg:order-1">
            <ParallaxImage
              src="/images/inicio/quienes-somos.webp"
              alt="Equipo Intercasa - Compromiso con lo que te gusta"
              speed={0.3}
              scale={false}
              className="absolute inset-0 h-full"
              imgClassName="scale-[0.55]"
              overlay={true}
              overlayOpacity={0.1}
            />
          </div>

          {/* Columna de texto */}
          <div className="flex items-center justify-center p-8 md:p-16 lg:p-24 order-1 lg:order-2 bg-intercasa-bone">
            <div className="max-w-xl">
              <AnimatedText
                text="Compromiso con lo que te gusta"
                as="h2"
                animation="words"
                className="font-baskerville font-bold text-intercasa-dark text-4xl md:text-5xl lg:text-6xl mb-8"
              />
              
              <div className="space-y-6 story-text">
                <p className="font-lato text-lg md:text-xl text-intercasa-dark/80 leading-relaxed">
                  Intercasa nace del deseo de mantener vivos los sabores de siempre 
                  y acercarlos a las familias canarias con la misma dedicación 
                  con la que se han elaborado toda la vida.
                </p>
                <p className="font-lato text-lg md:text-xl text-intercasa-dark/80 leading-relaxed">
                  Elaboramos nuestros productos en Canarias, con un equipo comprometido 
                  y procesos que garantizan calidad y sabor en cada lote.
                </p>
              </div>

              <AnimatedSection animation="fadeUp" delay={0.3} className="mt-10">
                <Link to="/nosotros">
                  <MagneticButton variant="primary" size="lg">
                    Quiénes somos
                  </MagneticButton>
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. BLOQUE DE CATEGORÍAS DE PRODUCTOS
        Grid de categorías con imagen, nombre y botón
        =============================================
      */}
      <section className="relative bg-intercasa-red py-24 md:py-32 overflow-hidden">
        {/* Fondo decorativo */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-intercasa-yellow rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          {/* Título de sección */}
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl mb-4">
              Nuestra gama de productos
            </h2>
            <p className="text-center font-lato text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              Salsas y productos elaborados con el sabor auténtico de Canarias
            </p>
          </AnimatedSection>

          {/* Grid de categorías - 2 columnas según guía */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {productCategories.map((category, index) => (
              <AnimatedSection
                key={category.id}
                animation="scale"
                delay={index * 0.15}
                className="group"
              >
                <Link 
                  to={category.link}
                  className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 relative opacity-100 hover:opacity-100"
                >
                  {/* Imagen de categoría */}
                  <div className="aspect-[16/10] bg-intercasa-bone overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-8">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-2 group-hover:text-intercasa-red transition-colors">
                      {category.name}
                    </h3>
                    <p className="font-lato text-intercasa-dark/60 text-lg mb-6">
                      {category.description}
                    </p>
                    <span className="inline-flex items-center font-lato font-semibold text-intercasa-red group-hover:gap-3 gap-2 transition-all">
                      Ver categoría
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </div>

                  {/* Barra decorativa amarilla en hover */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. BLOQUE RECETAS DESTACADAS
        Tres recetas en cards (Imagen, Título, Enlace)
        =============================================
      */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              Inspiración culinaria
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-4xl md:text-5xl lg:text-6xl mb-4">
              Recetas para disfrutar cada día
            </h2>
            <p className="text-center font-lato text-intercasa-dark/60 text-lg max-w-2xl mx-auto">
              Descubre preparaciones tradicionales y nuevas ideas para sacar 
              el máximo partido a nuestros productos
            </p>
          </AnimatedSection>

          {/* Grid de recetas - 3 cards según guía */}
          <div className="grid md:grid-cols-3 gap-8">
            {featuredRecipes.map((recipe, index) => (
              <AnimatedSection
                key={recipe.id}
                animation="fadeUp"
                delay={index * 0.15}
              >
                <Link 
                  to={recipe.url}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-2xl bg-intercasa-bone shadow-lg hover:shadow-2xl transition-shadow duration-500">
                    {/* Imagen */}
                    <div className="aspect-[4/3] overflow-hidden">
                      {recipe.imageUrl ? (
                        <img 
                          src={recipe.imageUrl} 
                          alt={recipe.imageAlt || recipe.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-intercasa-bone flex items-center justify-center group-hover:scale-110 transition-transform duration-700">
                          <svg className="w-20 h-20 text-intercasa-red opacity-30" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Contenido */}
                    <div className="p-6 bg-white h-56">
                      <span className="font-lato text-intercasa-red text-xs uppercase tracking-wider">
                        {recipe.category}
                      </span>
                      <h3 className="font-baskerville font-semibold text-xl md:text-2xl text-intercasa-dark mt-2 group-hover:text-intercasa-red transition-colors">
                        {recipe.title}
                      </h3>
                      <p className="font-lato text-intercasa-dark/60 mt-2 text-left text-base leading-relaxed">
                        {recipe.description}
                      </p>
                    </div>

                    {/* Línea decorativa */}
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fadeUp" delay={0.4} className="text-center mt-12">
            <Link to="/recetas">
              <MagneticButton variant="primary" size="lg">
                Ver todas las recetas
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* 
        =============================================
        5. BLOQUE DE CIERRE EMOCIONAL
        Título: "Somos Intercasa. Somos Canarias."
        CTA: "Conócenos mejor"
        =============================================
      */}
      <section 
        ref={ctaRef}
        className="relative py-24 md:py-32 overflow-hidden"
      >
        {/* Imagen de fondo */}
        <div className="absolute inset-0">
          <img 
            src="/images/inicio/somos-intercasa.webp" 
            alt="Somos Intercasa" 
            className="w-full h-full object-cover"
          />
          {/* Overlay rojo */}
          <div className="absolute inset-0 bg-intercasa-red/85"></div>
        </div>

        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-intercasa-yellow/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>

        {/* Contenido */}
        <div className="relative z-10 w-full">
          <div className="container mx-auto px-4 md:px-8 text-center max-w-4xl">
            <AnimatedText
              text="Somos Intercasa. Somos Canarias."
              as="h2"
              animation="words"
              className="font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-7xl mb-8"
            />
            
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <p className="text-center font-lato text-xl md:text-2xl text-white/90 mb-6 leading-relaxed">
                En Intercasa creemos que el sabor también cuenta historias. 
                Cada producto que elaboramos lleva un poco de nuestra tierra, 
                de su tradición y de la forma de vivir que nos hace únicos.
              </p>
            </AnimatedSection>

            {/* Estadísticas - Datos reales según guía (fundación 1949) */}
            <AnimatedSection animation="fadeUp" delay={0.4}>
              <div className="grid grid-cols-3 gap-8 my-12 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="font-baskerville font-bold text-intercasa-yellow text-4xl md:text-5xl">
                    1949
                  </div>
                  <div className="font-lato text-white/70 text-sm uppercase tracking-wider mt-1">
                    Fundación
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-baskerville font-bold text-intercasa-yellow text-4xl md:text-5xl">
                    100%
                  </div>
                  <div className="font-lato text-white/70 text-sm uppercase tracking-wider mt-1">
                    Canario
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-baskerville font-bold text-intercasa-yellow text-4xl md:text-5xl">
                    75+
                  </div>
                  <div className="font-lato text-white/70 text-sm uppercase tracking-wider mt-1">
                    Años
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.5}>
              <Link to="/nosotros">
                <MagneticButton variant="white" size="xl">
                  Conócenos mejor
                </MagneticButton>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/*
        =============================================
        6. CARRUSEL DE SUBVENCIONES
        Muestra las subvenciones recibidas
        =============================================
      */}
      <SubsidiesGrid />

    </ImmersiveLayout>
  );
};

/**
 * Datos de las subvenciones
 */
const subsidiesData = [
  {
    image: '/images/subvenciones/Subvencion3.webp',
    alt: 'Subvención al transporte marítimo y aéreo de mercancías con origen o destino en las Islas Canarias',
    pdf: '/images/subvenciones/PDF/SubvencionesMaritimo.pdf',
    odt: '/images/subvenciones/PDF/SubvencionesMaritimo.docx',
  },
  {
    image: '/images/subvenciones/Cartel Intercas Autoconsumo.jpg',
    alt: 'Instalación de autoconsumo fotovoltaico en cubierta',
    pdf: '/images/subvenciones/PDF/Cartel Intercas Autoconsumo.pdf',
    odt: '/images/subvenciones/PDF/Autoconsumo.odt',
  },
  {
    image: '/images/subvenciones/Cartel Intercasa Prórroga.jpg',
    alt: 'Instalación fotovoltaica de autoconsumo de 100 kW con almacenamiento y mejora de los procesos productivos',
    pdf: '/images/subvenciones/PDF/Cartel Intercasa Prórroga.pdf',
    odt: '/images/subvenciones/PDF/Prorroga.docx',
  },
];

/**
 * Componente de cuadrícula de subvenciones - muestra todos los carteles a la vez
 */
const SubsidiesGrid = () => {
  return (
    <section className="relative py-20 md:py-28 bg-intercasa-bone/30 overflow-hidden">
      {/* Decoración */}
      <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-intercasa-red/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-intercasa-yellow/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 px-4 md:px-6">
        {/* Encabezado */}
        <AnimatedSection animation="fadeUp">
          <div className="text-center mb-12">
            <span className="inline-block font-lato text-intercasa-red text-sm uppercase tracking-[0.2em] font-medium mb-4">
              Portal de Transparencia
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl mb-4">
              Ayudas y Subvenciones
            </h2>
            <p className="font-lato text-intercasa-dark/60 text-lg max-w-2xl mx-auto">
              Comprometidos con la transparencia. Estas son las ayudas y subvenciones que hemos recibido.
            </p>
          </div>
        </AnimatedSection>

        {/* Carteles de subvenciones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto">
          {subsidiesData.map((sub, i) => (
            <AnimatedSection key={i} animation="fadeUp" delay={0.1 + i * 0.15}>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-intercasa-bone hover:shadow-xl transition-shadow duration-300">
                {/* Imagen del cartel */}
                <div className="bg-white p-6 md:p-8 flex items-center justify-center">
                  <img
                    src={sub.image}
                    alt={sub.alt}
                    className="w-full h-auto object-contain rounded-lg"
                  />
                </div>

                {/* Botones de descarga */}
                <div className="flex justify-center gap-4 p-5 border-t border-intercasa-bone/50">
                  <a
                    href={sub.pdf}
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium text-sm"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar PDF
                  </a>
                  <a
                    href={sub.odt}
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium text-sm"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar ODT
                  </a>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Enlace a página completa */}
        <AnimatedSection animation="fadeUp" delay={0.6}>
          <div className="text-center mt-10">
            <Link to="/transparencia/ayudas">
              <MagneticButton variant="primary" size="lg">
                Ver todas las subvenciones
              </MagneticButton>
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default HomePage;